/**
 * Store pour les tâches du client affecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useAuthPlanningSubjectsValidationStore } from '@stores/auth/planning/subjects/validation';
import { useAuthCustomerPlanningStatusStore } from '@stores/auth/planning/customerPlanningStatus';
import { useAuthWritingTaskDraftStore } from '@stores/auth/writingTask/draft';
import { useHorseChestnutEventStore } from '@stores/horseChestnutEvent';
import { useAuthCompletedTaskStore } from '@stores/auth/completedTask';

export const useAuthCustomerTaskStore = defineStore('authCustomerTask', (() => {

    // Store pour la validation des sujets de publication de l'Intelligence Artificielle
    const validationSubjectsStore = useAuthPlanningSubjectsValidationStore();
    // Store pour les événements du marronnier
    const horseChestnutEventStore = useHorseChestnutEventStore();
    // Store pour la mise à jour du statut des plannings (IA)
    const authCustomerPlanningStatusStore = useAuthCustomerPlanningStatusStore();
    // Store pour les brouillons
    const taskDraftStore = useAuthWritingTaskDraftStore();
    // Store pour les tâches complètes
    const completedTaskStore = useAuthCompletedTaskStore();

    // State des tâches chargées
    const customerTasks = ref([]);

    // State de la liste des tâches (total, tasks)
    const customerTaskList = ref({});

    // State des tâches visualisées par l'utilisateur
    const customerTasksViewed = ref([]);

    /**
     * Retourne la liste des tâches (items et total)
     * @return object
     */
    const getCustomerTaskList = computed(() => customerTaskList.value || {});

    /**
     * Retourne les tâches chargées
     * @return array
     */
    const getCustomerTasks = computed(() => customerTasks.value);

    /**
     * Retourne la tâche dont l'identifiant public est en paramètre
     * @param int publicId
     * @return object|null
     */
    const getCustomerTaskByPublicId = computed(() => {
        return ((publicId) => {
            const task = customerTasks.value.filter((taskFromState) => taskFromState.publicId === publicId).at(0);
            return (task ?? null);
        });
    });

    /**
     * Retourne si la tâche a été visualisée par l'utilisateur
     * @param object task
     * @return boolean
     */
    const getTaskView = computed(() => {
        return ((task) => {
            return (customerTasksViewed.value.indexOf(task.publicId) !== -1);
        });
    });

    /**
     * Chargement de la liste des tâches du client connecté
     * @param object params Paramètres pour la requête Ajax
     * @param bool resetList Vrai si la liste doit être réinitialisé
     * @return Promise
     */
    const loadCustomerTaskList = ((params, resetList = true) => {

        const path = '/api/me/tasks';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(resetList && params.offset === 0) {
                    resetCustomerTaskList();
                }

                if(resetList && params.start && params.end) {
                    reset();
                }

                updateCustomerTaskList(response.data.data);

                if(response.data.planningStatus) {
                    authCustomerPlanningStatusStore.updatePlanningStatus(response.data.planningStatus);

                    const initializedDate = authCustomerPlanningStatusStore.getPlanningStatusInitializationTaskListDate(response.data.planningStatus);
                    if(initializedDate === null) {
                        authCustomerPlanningStatusStore.setPlanningStatusInitializationTaskListDate(response.data.planningStatus, new Date());
                    }

                }

                if(Array.isArray(response.data.horseChestnutEvents)) {
                    response.data.horseChestnutEvents.forEach((horseChestnutEvent) => {
                        horseChestnutEventStore.updateEvent(horseChestnutEvent);
                    });
                }

                if(response.data.validationSubjects) {
                    const validationExisting = validationSubjectsStore.getValidationByToken(response.data.validationSubjects.token);
                    if(! validationExisting) { // Pour ne pas écraser les sujets
                        validationSubjectsStore.updateValidation(response.data.validationSubjects);
                    }
                }

                return Promise.resolve(response.data);
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement des données d'une tâche par identifiant public
     * @param int publicId
     * @param bool forceLoad Vrai si on doit forcer le rechargement de la tâche
     * @return Promise
     */
    const loadCustomerTaskByPublicId = ((publicId, forceLoad = false) => {

        const loadedTask = getCustomerTaskByPublicId.value(publicId);
        if(! forceLoad && loadedTask !== null) {
            return Promise.resolve();
        }

        const path = '/api/me/tasks/:publicId'.replace(':publicId', publicId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Duplication de la tâche en paramètre
     * @param object task
     * @return Promise
     */
    const duplicate = ((task) => {

        const path = '/api/me/tasks/:publicId/duplicate'.replace(':publicId', task.publicId);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setTimeout(() => {
                    updateCustomerTask(response.data.task);
                }, 200);

                return Promise.resolve(response.data.task);
            }
            return Promise.reject();
        });

    });

    /**
     * Supprime la tâche en paramètre
     * @param object task La tâche à supprimer
     * @return Promise
     */
    const removeTask = ((task) => {

        const path = '/api/me/tasks/:taskPublicId'.replace(':taskPublicId', task.publicId);

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                // Utilisation d'un timer pour exécuter les événements des composants après la suppression, avant la suppression dans le store
                setTimeout(() => {
                    onRemoveTask(task);
                }, 200);

                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Annule la publication d'une tâche
     * @param object task La tâche dont on annule la publication
     * @return Promise
     */
    const cancelPublication = ((task) => {

        const path = '/api/me/tasks/:taskPublicId/cancel-publication'.replace(':taskPublicId', task.publicId);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {

                // On met à jour le store en décalé pour pouvoir appeler les événements des composants avant
                setTimeout(() => {
                    completedTaskStore.forgetTask(response.data.task);
                    updateCustomerTask(response.data.task);
                }, 200);

                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Supprime un média du briefing
     * @param object task La tâche à gérer
     * @param objetc file Le fichier à supprimer
     * @return Promise
     */
    const removeBriefingFile = ((task, file) => {

        const fileId = (file.small) ? file.small.id : file.id;
        const path = '/api/me/tasks/:taskPublicId/medias/:fileId'
            .replace(':taskPublicId', task.publicId)
            .replace(':fileId', fileId)
        ;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {

                task.suggestedMedias = task.suggestedMedias.filter((currentMedia) => {
                    const currentFileId = (currentMedia.file.original) ? currentMedia.file.original.id : currentMedia.file.id;
                    return (currentFileId !== fileId);
                });

                task.briefing.files = task.briefing.files.filter((currentFile) => {
                    const currentFileId = (currentFile.small) ? currentFile.small.id : currentFile.id;
                    return (currentFileId !== fileId);
                });
                updateCustomerTask(task);
                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Change la valeur d'une tâche
     * @param object task La tâche à modifier
     * @param object requestParams Données à transmettre en Ajax
     * @return Promise
     */
    const changeTaskDatum = ((task, requestParams) => {

        const path = '/api/me/tasks/:taskPublicId/datum'.replace(':taskPublicId', task.publicId);

        return axios.post(path, requestParams).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Modifie si le média suggéré en paramètre a été choisi
     * @param string taskPublicId
     * @param object media
     * @return Promise
     */
    const toggleChooseSuggestedMedia = ((taskPublicId, media) => {

        const path = '/api/me/tasks/:taskPublicId/suggested-medias/:mediaId/toggle-choose'
            .replace(':taskPublicId', taskPublicId)
            .replace(':mediaId', media.id)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Modifie si l'accroche pour la langue et l'index en paramètre a été sélectionné
     * @param object task
     * @param string language
     * @param int hookIndex
     * @param string hook
     * @return Promise
     */
    const toggleSelectedHook = ((task, language, hookIndex, hook) => {

        const path = `/api/me/tasks/${ task.publicId }/hooks/toggle-select`;

        return axios.post(path, {
            language: language,
            hookIndex: hookIndex,
            hook: hook
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.newHooks) {
                const currentTask = Object.assign({}, JSON.parse(JSON.stringify(task)));
                currentTask.briefing.hooks = response.data.newHooks;
                updateCustomerTask(currentTask);
                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Echange des positions des élements du carrousel en paramètres pour le média suggéré
     * @param object task
     * @param object media
     * @param array templates
     * @return Promise
     */
    const exchangeSuggestedMediaCarouselItemPositions = ((task, media, templates) => {

        const path = '/api/me/tasks/:taskPublicId/suggested-medias/:mediaId/carousel-template/positions/exchange'
            .replace(':taskPublicId', task.id)
            .replace(':mediaId', media.id)
        ;

        const requestParams = {
            templates: templates
        };

        return axios.post(path, requestParams).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Change un élément de carrousel d'un média suggéré
     * @param Object task
     * @param Object media
     * @param int childIndex Index de l'item de carrousel
     * @param Object childItemTemplate Le template CAROUSEL_ITEM à affecter
     * @return void
     */
    const changeSuggestedMediaCarouselItem = ((task, media, childIndex, childItemTemplate) => {

        const taskCopy = Object.assign({}, { ...JSON.parse(JSON.stringify(task)) });
        const mediaCopy = Object.assign({}, { ...JSON.parse(JSON.stringify(media)) });

        const mediaIndex = taskCopy.suggestedMedias.findIndex((suggestedMedia) => suggestedMedia.id === mediaCopy.id);
        if(mediaIndex === -1) {
            return;
        }

        const currentTemplate = mediaCopy.graphicalLineTemplate;
        if(currentTemplate?.type !== 'CAROUSEL') {
            return;
        }

        mediaCopy.graphicalLineTemplate.children[childIndex] = { ...childItemTemplate };
        taskCopy.suggestedMedias[mediaIndex] = { ...mediaCopy };

        updateCustomerTask(taskCopy);
    });

    /**
     * Prépare la tâche pour effectuer un retour
     * @param object task
     * @return Promise
     */
    const prepareTaskSuggestionForReturn = ((task) => {

        const path = '/api/me/tasks/:taskPublicId/suggestion/return/prepare'
            .replace(':taskPublicId', task.id)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Modifie une tâche dans le store
     * @param object task Données de la tâche à mettre à jour dans le store
     * @return Promise
     */
    const updateCustomerTask = ((task) => {

        const currentTasks = customerTasks.value;

        // Mise à jour dans les tâches chargées
        const index = currentTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(index !== -1) {
            customerTasks.value[index] = { ...customerTasks.value[index], ...task };
        } else {
            customerTasks.value.push(task);
        }

        // Mise à jour dans la liste
        const currentListData = customerTaskList.value.tasks || [];
        const indexFromList = currentListData.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexFromList !== -1) {
            const currentTaskFromList = customerTaskList.value.tasks[indexFromList];
            customerTaskList.value.tasks[indexFromList] = { ...currentTaskFromList, ...task };
        }

    });

    /**
     * Modifie les médias suggérés de la tâche dans le store
     * @param Object task
     * @return void
     */
    const updateCustomerTaskSuggestedMedia = ((task) => {
        const currentTasks = customerTasks.value;

        // Mise à jour dans les tâches chargées
        const index = currentTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId);

        if(index === -1) {
            console.log('Task inconnue');
            return;
        }

        const currentTask = Object.assign({}, JSON.parse(JSON.stringify(currentTasks[index])));
        currentTask.suggestedMedias = [ ...task.suggestedMedias ];
        updateCustomerTask(currentTask);
    });

    /**
     * Ajoute un média suggéré à la tâche en paramètre
     * @param Object task
     * @param Object suggestedMedia
     * @return void
     */
    const addCustomerTaskSuggestedMedia = ((task, suggestedMedia) => {
        const currentTasks = customerTasks.value;

        // Index de la tâche dans la liste
        const index = currentTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(index === -1) {
            console.log('Task inconnue');
            return;
        }

        const currentTask = Object.assign({}, JSON.parse(JSON.stringify(currentTasks[index])));
        currentTask.suggestedMedias.push(suggestedMedia);

        currentTask.suggestedMedias = [ ...currentTask.suggestedMedias ];

        updateCustomerTask(currentTask);
    });

    /**
     * Méthode appelée après la suppression de la tâche en paramètre
     * @param object task Tâche supprimée
     * @return Promise
     */
    const onRemoveTask = ((task) => {

        // Mise à jour dans les tâches chargées
        const indexToDelete = customerTasks.value.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexToDelete !== -1) {
            customerTasks.value.splice(indexToDelete, 1);
        }

        // Mise à jour dans la liste
        const currentListData = customerTaskList.value.tasks || [];
        const indexFromList = currentListData.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexFromList !== -1) {
            customerTaskList.value.tasks.splice(indexFromList, 1);
            customerTaskList.value.total = Math.max(0, customerTaskList.value.total - 1);
        }

        taskDraftStore.onRemoveTask(task);
    });

    /**
     * Modifie la liste des tâches dans le store
     * @param data Données des tâches : total et items
     * @return void
     */
    const updateCustomerTaskList = ((data) => {

        const currentData = customerTaskList.value;
        if(currentData.total === undefined) {
            customerTaskList.value = {
                total: data.total,
                tasks: data.items
            };

            data.items.forEach((task) => {
                updateCustomerTask(task);
            });

        } else {
            customerTaskList.value.total = data.total;

            const currentTasks = customerTaskList.value.tasks;
            const newTasks = data.items.filter((item) => ! currentTasks.find((current) => current.id === item.id));
            customerTaskList.value.tasks = [ ...currentTasks, ...newTasks ];

            newTasks.forEach((task) => {
                updateCustomerTask(task);
            });
        }

    });

    /**
     * Enregistre que l'utilisateur a modifié la tâche
     * @param Object task
     * @return Promise
     */
    const save = ((task) => {

        const path = `/api/me/tasks/${ task.publicId }/save`;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success) {
                // On ne fait rien de particulier : les enregistrements de la tâche se font déjà au moment des actions
                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Met à jour la description du sujet de la tâche
     * @param object task
     * @param string subjectDescription
     * @return Promise
     */
    const updateTaskSubjectDescription = ((task, subjectDescription) => {
        const subject = task.aiSubject;

        const path = `/api/me/subjects/${ subject.id }/update-description`;

        return axios.post(path, {
            description: subjectDescription
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.subject) {

                const newTask = Object.assign({}, JSON.parse(JSON.stringify(task)));
                newTask.aiSubject = response.data.subject;
                updateCustomerTask(newTask);

                return Promise.resolve();
            }

            return Promise.reject({});
        }).catch((response) => {
            const errors = response?.response?.data?.errors ?? {};
            return Promise.reject(errors);
        });
    });

    /**
     * Active ou désactive le compte du réseau en paramètre pour la tâche en paramètre
     * @param object task
     * @param string network (twitter, facebook)
     * @param object account Le compte à activer ou désactiver
     * @return Promise
     */
    const toggleEnableNetworkAccount = ((task, network, account) => {

        const path = `/api/me/tasks/${ task.publicId }/networks/${ network }/accounts/${ account.api_id }/toggle-enable`;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                // Mise à jour des réseaux de la tâche dans le store
                const newTask = Object.assign({}, JSON.parse(JSON.stringify(task)));
                const newNetworks = response.data.task.briefing.networks;
                newTask.briefing.networks = newNetworks;
                updateCustomerTask(newTask);

                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie le type de tâche par celui en paramètre
     * @param object task
     * @param string newType
     * @return Promise
     */
    const changeTaskType = ((task, newType) => {

        const path = `/api/me/tasks/${ task.publicId }/change-type`;

        return axios.post(path, {
            taskType: newType
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateCustomerTask(response.data.task);
                return Promise.resolve();
            }

            return Promise.reject({});
        }).catch((response) => {
            const errors = response?.response?.data?.errors ?? {};
            return Promise.reject(errors);
        })
    });

    /**
     * Ajoute au store la tâche aux tâches vues par l'utilisateur
     * @param object task
     * @return void
     */
    const setTaskView = ((task) => {
        const index = customerTasksViewed.value.indexOf(task.publicId);
        if(index === -1) {
            customerTasksViewed.value.push(task.publicId);
        }
    });

    /**
     * Réinitialisation de la liste tâches
     * @return void
     */
    const resetCustomerTaskList = (() => {
        customerTaskList.value = {};
    });

    /**
     * Réinitialisation des tâches
     * @return void
     */
    const reset = (() => {
        customerTasks.value = [];
        customerTaskList.value = {};
    });

    return {
        getTaskView,
        setTaskView,
        getCustomerTasks,
        getCustomerTaskList,
        getCustomerTaskByPublicId,
        loadCustomerTaskList,
        loadCustomerTaskByPublicId,
        updateCustomerTask,
        reset,
        duplicate,
        changeTaskType,
        changeTaskDatum,
        removeTask,
        cancelPublication,
        removeBriefingFile,
        toggleChooseSuggestedMedia,
        toggleSelectedHook,
        prepareTaskSuggestionForReturn,
        exchangeSuggestedMediaCarouselItemPositions,
        changeSuggestedMediaCarouselItem,
        updateTaskSubjectDescription,
        toggleEnableNetworkAccount,
        save,
        updateCustomerTaskSuggestedMedia,
        addCustomerTaskSuggestedMedia
    };

}));


